import React, { useEffect, useRef, useState } from 'react';
import { CheckCircle2, ExternalLink, Loader2, Smartphone, Trash2, UploadCloud } from 'lucide-react';
import toast from 'react-hot-toast';
import Sidebar from '../../components/Sidebar';
import Navbar from '../../components/Navbar';
import API from '../../utils/api';
import { useConfirmDialog } from '../../hooks/useConfirmDialog';
import './AppLinks.css';

const STORES = [
    { key: 'googlePlay', label: 'Google Play link', placeholder: 'https://play.google.com/store/apps/details?id=...' },
    { key: 'appStore', label: 'App Store link', placeholder: 'https://apps.apple.com/app/...' },
    { key: 'androidLink', label: 'Android app download link (optional)', placeholder: 'https://example.com/gettimelam.apk', hint: 'Any direct download link for the Android app. If you also upload the app file below, the uploaded file is used.' },
];
const CHUNK_SIZE = 512 * 1024; // each piece stays well under the usual 1 MB request limit
const EMPTY = { googlePlay: '', appStore: '', androidLink: '' };

const isValid = value => !value.trim() || /^https?:\/\/\S+$/i.test(value.trim());
const mb = bytes => `${(bytes / (1024 * 1024)).toFixed(1)} MB`;

const AppLinks = () => {
    const [confirm, confirmDialog] = useConfirmDialog();
    const [links, setLinks] = useState(EMPTY);
    const [saved, setSaved] = useState(EMPTY);
    const [apk, setApk] = useState(null);
    const [saving, setSaving] = useState(false);
    const [progress, setProgress] = useState(null); // null = not uploading, otherwise 0-100
    const fileRef = useRef(null);

    const apply = data => {
        const next = { googlePlay: data.googlePlay || '', appStore: data.appStore || '', androidLink: data.androidLink || '' };
        setLinks(next);
        setSaved(next);
        setApk(data.apk || null);
    };

    useEffect(() => {
        API.get('/app-links/admin').then(res => apply(res.data)).catch(() => toast.error('Failed to load the app links'));
    }, []);

    const dirty = STORES.some(s => links[s.key].trim() !== saved[s.key]);
    const invalid = STORES.some(s => !isValid(links[s.key]));

    const save = async event => {
        event.preventDefault();
        if (invalid) return;
        setSaving(true);
        try {
            const res = await API.put('/app-links', { googlePlay: links.googlePlay.trim(), appStore: links.appStore.trim(), androidLink: links.androidLink.trim() });
            apply(res.data);
            toast.success('App links saved. The website footer is updated.');
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to save the app links');
        } finally {
            setSaving(false);
        }
    };

    const upload = async event => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file) return;
        if (!/\.apk$/i.test(file.name)) { toast.error('Please choose an Android app file ending in .apk'); return; }
        if (file.size > 300 * 1024 * 1024) { toast.error(`This file is ${(file.size / 1024 / 1024).toFixed(0)} MB. The maximum for the Android app is 300 MB.`, { duration: 6000 }); return; }
        // The file goes up in small pieces so it passes any web server limit on the size of one request.
        const total = Math.ceil(file.size / CHUNK_SIZE);
        const uploadId = Array.from(crypto.getRandomValues(new Uint8Array(12)), b => b.toString(16).padStart(2, '0')).join('');
        setProgress(0);
        try {
            let result = null;
            for (let index = 0; index < total; index += 1) {
                const offset = index * CHUNK_SIZE;
                const form = new FormData();
                form.append('uploadId', uploadId);
                form.append('index', index);
                form.append('total', total);
                form.append('offset', offset);
                form.append('size', file.size);
                form.append('name', file.name);
                form.append('chunk', file.slice(offset, offset + CHUNK_SIZE), 'chunk');
                // a piece that fails because of the network is tried again a few times
                for (let attempt = 1; ; attempt += 1) {
                    try { result = await API.post('/app-links/android-apk/chunk', form, { timeout: 60000 }); break; }
                    catch (error) { if (attempt >= 3 || error.response?.status < 500) throw error; await new Promise(r => setTimeout(r, 800 * attempt)); }
                }
                setProgress(Math.round(((index + 1) / total) * 100));
            }
            apply(result.data);
            toast.success('Android app uploaded. The footer button now downloads it.');
        } catch (error) {
            toast.error(error.response?.data?.message || (error.response ? 'Upload failed. Please try again.' : 'The upload could not reach the server. Please check your internet connection and try again.'));
        } finally {
            setProgress(null);
        }
    };

    const removeApk = async () => {
        if (!(await confirm({
            title: 'Remove the uploaded Android app?',
            message: 'The website footer button will fall back to the download link, or show "Coming soon".',
            confirmLabel: 'Remove app', danger: true, icon: Trash2,
        }))) return;
        try {
            const res = await API.delete('/app-links/android-apk');
            apply(res.data);
            toast.success('Uploaded app removed');
        } catch (error) {
            toast.error(error.response?.data?.message || 'Could not remove the file');
        }
    };

    const uploading = progress !== null;

    return (
        <div className="applinks-layout">
            {confirmDialog}
            <Sidebar />
            <div className="admin-content applinks-content">
                <Navbar title={<span className="applinks-nav-title"><Smartphone size={18} /> App downloads</span>} />
                <main className="applinks-page">
                    <header className="applinks-head">
                        <h1>App downloads</h1>
                        <p>These appear as download buttons in the website footer. Paste a store link, or upload the Android app file below so visitors can download and install it straight from your site. Leave a link empty and that button shows “Coming soon”.</p>
                    </header>

                    <form className="applinks-card" onSubmit={save}>
                        {STORES.map(store => (
                            <label className="applinks-field" key={store.key}>
                                <span>{store.label}</span>
                                <div className="applinks-input">
                                    <input
                                        type="url"
                                        value={links[store.key]}
                                        placeholder={store.placeholder}
                                        onChange={event => setLinks(current => ({ ...current, [store.key]: event.target.value }))}
                                        aria-invalid={!isValid(links[store.key])}
                                    />
                                    {saved[store.key] && (
                                        <a href={saved[store.key]} target="_blank" rel="noopener noreferrer" title="Open the saved link"><ExternalLink size={16} /></a>
                                    )}
                                </div>
                                {!isValid(links[store.key]) && <small className="applinks-error">The link must start with http:// or https://</small>}
                                {store.hint && <small className="applinks-state">{store.hint}</small>}
                                <small className="applinks-state">
                                    {saved[store.key] ? <><CheckCircle2 size={13} /> Saved</> : 'Not set'}
                                </small>
                            </label>
                        ))}
                        <div className="applinks-actions">
                            <button type="submit" disabled={saving || !dirty || invalid}>{saving ? 'Saving…' : 'Save links'}</button>
                        </div>
                    </form>

                    <section className="applinks-card applinks-upload">
                        <div>
                            <h2>Upload the Android app</h2>
                            <p>Upload the <b>.apk</b> file (up to 300 MB). When someone taps the footer button on their phone, the file downloads automatically and they tap it to install. Uploading a new file replaces the old one.</p>
                        </div>

                        {apk ? (
                            <div className="applinks-file">
                                <Smartphone size={22} />
                                <div>
                                    <strong>{apk.name}</strong>
                                    <small>{mb(apk.size)}{apk.uploadedAt ? ` · uploaded ${new Date(apk.uploadedAt).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}` : ''}</small>
                                    <small className="applinks-live"><CheckCircle2 size={13} /> Live: the website footer downloads this file</small>
                                </div>
                                <a href={`${API.defaults.baseURL}/app-links/download`} title="Download this file"><ExternalLink size={16} /></a>
                                <button type="button" onClick={removeApk} disabled={uploading} title="Remove the uploaded file"><Trash2 size={16} /></button>
                            </div>
                        ) : (
                            <small className="applinks-state">No app file uploaded yet.</small>
                        )}

                        {uploading && (
                            <div className="applinks-progress" role="progressbar" aria-valuenow={progress} aria-valuemin={0} aria-valuemax={100}>
                                <div style={{ width: `${progress}%` }} />
                                <span>Uploading… {progress}%</span>
                            </div>
                        )}

                        <input ref={fileRef} type="file" accept=".apk,application/vnd.android.package-archive" hidden onChange={upload} />
                        <div className="applinks-actions">
                            <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading}>
                                {uploading ? <><Loader2 size={15} className="applinks-spin" /> Uploading…</> : <><UploadCloud size={16} /> {apk ? 'Replace app file' : 'Choose .apk file'}</>}
                            </button>
                        </div>
                    </section>
                </main>
            </div>
        </div>
    );
};

export default AppLinks;
